'use client';

import { useLazyLoginQuery } from '@/src/api/api';
import useParamsEditor from '@/src/utils/useParamsEditor';
import { useSearchParams } from 'next/navigation';
import QueryLink from '@components/QueryLink';
import { useLoginHandler } from '@/src/redux/userSlice/loginHandlers';
import { useState } from 'react';
import { userState } from '@/src/redux/userSlice/userSlice';
import { useTranslations } from 'next-intl';
import ModalTitle from '@components/ModalTitle';
import useATSName from '@/src/utils/useATSName';
import getOption from '@/src/utils/getOption';

export default function LoginForm() {
  const t = useTranslations('LoginForm');
  const ats = useATSName();
  const editParam = useParamsEditor();
  const afterRegistration = useSearchParams().get('registered');
  const afterPasswordReset = useSearchParams().get('password_changed');
  const [loginQuery, { data, isLoading }] = useLazyLoginQuery();
  const [errorMsg, setErrorMsg] = useState('');
  const loginHandler = useLoginHandler();

  if (!getOption('SHOW_USER_FUNCTIONS')) {
    return null;
  }

  const login = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');
    const form_data = new FormData(e.currentTarget);
    const form_values = Object.fromEntries(form_data);
    loginQuery(form_values)
      .unwrap()
      .then((res: any) => {
        if (res?.data?.access_token) {
          loginHandler({
            userId: res.data.user_id,
            accessToken: res.data.access_token,
            firstName: String(res.data.first_name),
            lastName: String(res.data.last_name),
          } as userState);
          editParam({ registered: null, modal: null });
        } else {
          setErrorMsg(
            res.message === 'messages.login.failed'
              ? t('loginFailed')
              : res.message
          );
        }
      })
      .catch((error: any) => {
        if ('error' in error) {
          setErrorMsg(error.error);
        } else if ('status' in error) {
          if (
            error.hasOwnProperty('data') &&
            error.data.hasOwnProperty('message')
          ) {
            setErrorMsg(
              error.data.message === 'messages.login.failed'
                ? t('loginFailed')
                : error.data.message
            );
          }
        }
      });
  };

  const openRegister = () => {
    editParam('modal', 'register');
  };
  return (
    <div className='m-auto md:max-w-[400px]'>
      <ModalTitle title={t('title')} />
      {afterRegistration && (
        <div className='mb-8 rounded-sm bg-green-700 p-4 font-semibold'>
          {t('accountCreated')}
        </div>
      )}
      {afterPasswordReset && (
        <div className='mb-8 rounded-sm bg-green-700 p-4 font-semibold'>
          {t('passwordReset')}
        </div>
      )}
      <form method='post' onSubmit={login} className='mb-8 w-full'>
        <div className='-mx-3 mb-6 flex flex-wrap'>
          <div className='w-full px-3'>
            <label
              htmlFor='email'
              className='mb-2 block text-sm font-bold uppercase'
            >
              {t('form.email.label')}
            </label>
            <input
              name='email'
              id='email'
              className='block w-full appearance-none rounded-sm p-2 text-dark'
              placeholder={t('form.email.placeholder')}
              required={true}
            />
          </div>
        </div>
        <div className='-mx-3 mb-6 flex flex-wrap'>
          <div className='w-full px-3'>
            <label
              htmlFor='password'
              className='mb-2 block text-sm font-bold uppercase'
            >
              {t('form.password.label')}
            </label>
            <input
              name='password'
              id='password'
              type='password'
              className='block w-full appearance-none rounded-sm p-2 text-dark'
              placeholder={t('form.password.placeholder')}
              required={true}
            />
          </div>
        </div>
        <div className='-mx-3 flex flex-wrap '>
          <div className='w-full px-3'>
            <button
              type='submit'
              className='mb-1 mt-4 w-full appearance-none rounded-sm border border-brand bg-brand p-2 font-bold uppercase text-black transition-all hover:bg-transparent hover:text-white'
            >
              {!isLoading ? t('isLoading.false') : t('isLoading.true')}
            </button>
            {errorMsg}
            {!afterRegistration && !afterPasswordReset && (
              <QueryLink
                query={{ modal: 'forgotten' }}
                className='mt-3 block appearance-none text-center text-xs underline hover:no-underline'
              >
                {t('forgotPassword')}
              </QueryLink>
            )}
          </div>
        </div>
      </form>
      {!afterRegistration && !afterPasswordReset && (
        <section className='border-t-[1px] border-t-white/30 pt-8'>
          <h2 className='mb-1 text-lg font-bold'>{t('noAccount')}</h2>
          <p>{t('noAccountInformation', { ats })}</p>
          <button
            onClick={openRegister}
            className='mt-8 w-full appearance-none rounded-sm border-[1px] p-2 font-bold uppercase underline transition-all hover:bg-white hover:text-dark'
          >
            {t('startRegistration')}
          </button>
        </section>
      )}
    </div>
  );
}
